import type { Timestamp } from '@bufbuild/protobuf';
import type { SupportAddonPrices as ProtoAddonPrices } from 'gportal-grpc/gportal/api/support/v1/support_addons_pb';
import type {
  SupportCategory as ProtoCategory,
  SupportCategoryServer as ProtoUserServer,
} from 'gportal-grpc/gportal/api/support/v1/support_infos_pb';
import type { SupportMessage as ProtoMessage } from 'gportal-grpc/gportal/api/support/v1/support_message_pb';
import type { Ticket as ProtoTicket } from 'gportal-grpc/gportal/api/support/v1/ticket_pb';
import type { Money as ProtoMoney } from 'gportal-grpc/gportal/type/v1/money_pb';
import type {
  Addon,
  AddonPrice,
  AddonPricing,
  AddonType,
  Ticket,
  TicketCategory,
  TicketMessage,
  TicketSource,
  UserServer,
} from '~/store/support/types';

// Type adapters for proto <-> app string unions ------------------------------

export type TicketStatus =
  | 'unspecified'
  | 'open'
  | 'closed'
  | 'answered'
  | 'followup'
  | 'pending';

export type MessageRating = 'unspecified' | 'positive' | 'negative';

// Status conversion maps
const STATUS_FROM_PROTO = new Map<number, TicketStatus>([
  [0, 'unspecified'],
  [1, 'open'],
  [2, 'closed'],
  [4, 'answered'],
  [8, 'followup'],
  [16, 'pending'],
] as const);

const STATUS_TO_PROTO = new Map<TicketStatus, number>([
  ['unspecified', 0],
  ['open', 1],
  ['closed', 2],
  ['answered', 4],
  ['followup', 8],
  ['pending', 16],
] as const);

// Rating conversion maps
const RATING_FROM_PROTO = new Map<number, MessageRating>([
  [0, 'unspecified'],
  [1, 'positive'],
  [-1, 'negative'],
] as const);

const RATING_TO_PROTO = new Map<MessageRating, number>([
  ['unspecified', 0],
  ['positive', 1],
  ['negative', -1],
] as const);

// Conversion functions -------------------------------------------------------

export const toStatus = (n: number): TicketStatus =>
  STATUS_FROM_PROTO.get(n) ?? 'unspecified';

export const toProtoStatus = (s: TicketStatus): number =>
  STATUS_TO_PROTO.get(s) ?? 0;

export const toRating = (n: number): MessageRating =>
  RATING_FROM_PROTO.get(n) ?? 'unspecified';

export const toProtoRating = (r: MessageRating): number =>
  RATING_TO_PROTO.get(r) ?? 0;

// TicketSource proto enum: 0 = unspecified (legacy), 1 = legacy, 2 = intercom. Anything that isn't
// explicitly intercom is treated as legacy, so legacy tickets (which leave source unset) map cleanly.
export const toSource = (protoSource: number): TicketSource =>
  protoSource === 2 ? 'intercom' : 'legacy';

export const toAddonType = (protoType: number): AddonType => {
  if (protoType === 1) return 'premium'; // SUPPORT_ADDON_TYPE_PREMIUM_SUPPORT
  if (protoType === 2) return 'priority'; // SUPPORT_ADDON_TYPE_PRIORITY_SUPPORT
  return 'standard'; // SUPPORT_ADDON_TYPE_UNSPECIFIED
};

// Timestamp formatting -------------------------------------------------------

export const formatTimestamp = (timestamp?: Timestamp | null): string => {
  if (!timestamp) return '';

  const seconds = Number(timestamp.seconds);
  const nanos = Number(timestamp.nanos || 0);

  return new Date(seconds * 1000 + nanos / 1_000_000).toISOString();
};

// Proto mappers --------------------------------------------------------------

export const mapProtoCategory = (category: ProtoCategory): TicketCategory => {
  const customerServers = (category.customerServers || []).filter(
    (s): s is NonNullable<typeof s> => !!s,
  );

  // Map availableAddons from the proto to our internal Addon type
  const addons: Addon[] = (category.availableAddons || []).map((addon) => ({
    type: toAddonType(addon.type),
    enabled: addon.isEnabled,
    reasonKey: addon.disableReasonLocoKey || '',
  }));

  // Determine if category has premium/priority tickets based on availableAddons
  const hasPremiumTickets = addons.some(
    (addon) => addon.type === 'premium' && addon.enabled,
  );
  const hasPriorityTickets = addons.some(
    (addon) => addon.type === 'priority' && addon.enabled,
  );

  return {
    id: category.id,
    parentId: category.parentId,
    name: category.name,
    locoKey: category.locoKey,
    hasPremiumTickets,
    hasPriorityTickets,
    customerServers: customerServers.map((server) => ({
      id: server.id,
      name: server.name,
      serverAddress: server.serverAddress,
    })),
    addons,
  };
};

export const mapProtoMessage = (msg: ProtoMessage): TicketMessage => ({
  id: msg.id,
  isCustomer: msg.isCustomer,
  message: msg.message,
  rating: toRating(msg.rating),
  created: formatTimestamp(msg.created),
  customer: {
    userName: msg.customer?.userName || '',
  },
  attachments: (msg.attachments ?? []).map((a) => ({
    name: a.name,
    contentType: a.contentType,
    url: a.url,
    // uint64 generates as a bigint (`protoInt64.zero`), which JSON.stringify refuses and arithmetic
    // will not mix with numbers. A file size fits a JS number comfortably, so narrow it here rather
    // than letting a bigint leak into the component.
    filesize: Number(a.filesize ?? 0),
  })),
});

const emptyCategory = (): TicketCategory => ({
  id: '',
  parentId: '',
  name: '',
  hasPremiumTickets: false,
  hasPriorityTickets: false,
  customerServers: [],
  addons: [],
});

export const mapProtoTicket = (ticket: ProtoTicket): Ticket => ({
  id: ticket.id,
  source: toSource(ticket.source),
  title: ticket.title,
  status: toStatus(ticket.status),
  // Intercom tickets may carry only a category name (or none); fall back to an empty category so the
  // UI never crashes on a missing nested proto.
  category: ticket.category
    ? mapProtoCategory(ticket.category)
    : emptyCategory(),
  isClosable: ticket.isCloseable,
  messages: ticket.messages.map(mapProtoMessage),
  created: formatTimestamp(ticket.created),
  emailNotified: formatTimestamp(ticket.emailNotified) || undefined,
  addon: ticket.addon
    ? {
        id: ticket.addon.id,
        type: toAddonType(ticket.addon.type),
        status: String(ticket.addon.status),
      }
    : undefined,
});

export const mapProtoAddonPricing = (
  protoAddonPrices: ProtoAddonPrices,
): AddonPricing => {
  // Map prices from proto
  const prices: Record<string, AddonPrice> = {};
  if (protoAddonPrices.prices) {
    // Access the prices map entries correctly
    const pricesMap = protoAddonPrices.prices as Record<string, ProtoMoney>;
    for (const [currencyCode, money] of Object.entries(pricesMap)) {
      prices[currencyCode] = {
        amount: money.amount.toString(),
        currencyCode: money.currencyCode,
        fraction: money.fraction,
      };
    }
  }

  // Map discounted prices from proto if present
  let discountedPrices: Record<string, AddonPrice> | undefined;
  if (protoAddonPrices.discountedPrices) {
    discountedPrices = {};
    const discountedMap = protoAddonPrices.discountedPrices as Record<
      string,
      ProtoMoney
    >;
    for (const [currencyCode, money] of Object.entries(discountedMap)) {
      discountedPrices[currencyCode] = {
        amount: money.amount.toString(),
        currencyCode: money.currencyCode,
        fraction: money.fraction,
      };
    }
  }

  return {
    type: toAddonType(protoAddonPrices.type),
    prices,
    discountedPrices,
    reasonForDiscountedPricesLocoKey:
      protoAddonPrices.reasonForDiscountedPricesLocoKey,
    remainingFreeTickets: protoAddonPrices.remainingFreeTickets,
  };
};

export const mapProtoUserServer = (server: ProtoUserServer): UserServer => ({
  id: server.id,
  name: server.name,
  serverAddress: server.serverAddress,
});

export default {
  toStatus,
  toProtoStatus,
  toRating,
  toProtoRating,
  toAddonType,
  formatTimestamp,
  mapProtoMessage,
  mapProtoTicket,
  mapProtoCategory,
  mapProtoAddonPricing,
  mapProtoUserServer,
};
